import React, {useState} from 'react';
import {formatDate} from '../../utils/dateUtils';
import '../card/eventCard.css'; // Reusing your existing CSS
import {PlaceActivityDetailDto} from '../../generated/models/PlaceActivityDetailDto';

type PlaceSummaryCardProps = React.FC<{
  place: PlaceActivityDetailDto;
  onDeletePlace: (placeId: string) => void;
  onClick: (placeId: string) => void;
  onEdit: (placeId: string) => void;
}>;

export const PlaceSummaryCard: PlaceSummaryCardProps = ({
  place,
  onDeletePlace,
  onClick,
  onEdit,
}) => {
  const [hoveredButton, setHoveredButton] = useState<'edit' | 'delete' | null>(
    null,
  );

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (window.confirm('Are you sure you want to delete this place?')) {
      onDeletePlace(place.id || '');
    }
  };

  const actionBarStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    marginBottom: '20px',
  };

  const buttonBaseStyle: React.CSSProperties = {
    padding: '10px 20px',
    borderRadius: '8px',
    border: 'none',
    cursor: 'pointer',
    fontWeight: 'bold',
    fontSize: '1em',
    transition: 'all 0.2s ease',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  };

  const editStyle: React.CSSProperties = {
    ...buttonBaseStyle,
    backgroundColor: hoveredButton === 'edit' ? '#cbd5e1' : '#e2e8f0',
    color: '#334155',
    transform: hoveredButton === 'edit' ? 'scale(1.05)' : 'scale(1)',
  };

  const deleteStyle: React.CSSProperties = {
    ...buttonBaseStyle,
    backgroundColor: hoveredButton === 'delete' ? '#fecaca' : '#fee2e2',
    color: '#991b1b',
    transform: hoveredButton === 'delete' ? 'scale(1.05)' : 'scale(1)',
  };

  return (
    <div
      onClick={() => onClick(place.id || '')}
      style={{cursor: 'pointer'}}
      className="event-card-container">
      <div style={actionBarStyle}>
        <button
          onMouseEnter={() => setHoveredButton('edit')}
          onMouseLeave={() => setHoveredButton(null)}
          onClick={e => {
            e.preventDefault();
            e.stopPropagation();
            onEdit(place.id || '');
          }}
          style={editStyle}>
          Edit
        </button>
        <button
          onMouseEnter={() => setHoveredButton('delete')}
          onMouseLeave={() => setHoveredButton(null)}
          onClick={handleDelete}
          style={deleteStyle}>
          Delete
        </button>
      </div>

      <div className="card-details">
        {/* A place without a photo showed a broken image. */}
        {(place.photoUris?.[0] || place.photoUri) && (
          <div className="card-image-wrapper">
            <img
              src={place.photoUris?.[0] || place.photoUri || ''}
              alt="Place"
              className="card-image"
            />
          </div>
        )}
        <div className="card-text">
          <h2>
            <span className="label">Place:</span> {place.title}
          </h2>
          {/* Specific Place Details */}
          <p>
            <span className="label">Rating:</span> ⭐{' '}
            {place.rating?.toFixed(1) || '0.0'} ({place.ratingCount || 0})
          </p>
          <p>
            <span className="label">Price Level:</span>{' '}
            {place.priceLevel || 'N/A'}
          </p>
          <p>
            <span className="label">City:</span> {place.address?.cityName}
          </p>
          <p>
            <span className="label">Street:</span> {place.address?.streetName},{' '}
            {place.address?.streetNumber}
          </p>
          <p>
            <span className="label">Phone:</span> {place.phoneNumber}
          </p>
        </div>
      </div>
    </div>
  );
};
